import type { IColumnDefsConfig } from "@focus/focusUI";
import ReactDOM from "react-dom/client";
import { getValueByPath, readFormat } from "../services/helpers/headers";
import type { ColumnFiltersState } from "../services/helpers/pager";

export type FormatterMap = Record<string, (value: number) => unknown>;

/**
 * Searchs for a sub-element within the given element, by going through an array of strings that indicate the path to the sub-element
 * @param element The element where the descendant is going to be searched for
 * @param descendantPathArray Array of strings defining the location of the descendant within the given element
 */
export const getElementDescendant = (
  element,
  descendantPathArray: string[],
) => {
  if (element && descendantPathArray.length > 0) {
    return getElementDescendant(
      element[descendantPathArray.shift()],
      descendantPathArray,
    );
  }
  return element;
};

export const applyFormat = (element, paramName, formatters) => {
  const formatter = formatters ? formatters[paramName] : undefined;
  const attributeRoute = paramName.split(".");

  if (paramName.endsWith(".value")) {
    attributeRoute.pop();
    const valueObject = getElementDescendant(element, attributeRoute);
    const formattedValue = formatter
      ? formatter(valueObject.value)
      : valueObject?.value;
    return { ...valueObject, value: formattedValue };
  } else {
    const valueObject = getElementDescendant(element, attributeRoute);
    const formattedValue = formatter
      ? formatter(valueObject.value)
      : valueObject.value;
    return formattedValue;
  }
};

export const formatValueWithOptionalUnit = (
  value: unknown,
  formatter?: (value: unknown) => unknown,
  unit?: string,
) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  const numericValue =
    typeof value === "number" ? value : Number.parseFloat(`${value}`);
  const formattedValue = formatter
    ? Number.isFinite(numericValue)
      ? formatter(numericValue)
      : formatter(value)
    : value;

  return unit ? `${formattedValue} ${unit}` : `${formattedValue}`;
};

export const getFormattedFieldDisplayValue = (
  element: unknown,
  field: string,
  formatters: unknown,
) => {
  if (field.endsWith(".value")) {
    const valueObject = getElementDescendant(element, field.split(".").slice(0, -1));
    const rawValue = (valueObject as { value?: unknown } | undefined)?.value;

    if (rawValue === undefined || rawValue === null || rawValue === "") {
      return "-";
    }
  }

  const formattedValue = applyFormat(element, field, formatters);

  if (
    formattedValue &&
    typeof formattedValue === "object" &&
    "value" in formattedValue &&
    Object.prototype.hasOwnProperty.call(formattedValue, "value")
  ) {
    const wrapped = formattedValue as { units?: unknown; value?: unknown };

    if (wrapped.value === undefined || wrapped.value === null || wrapped.value === "") {
      return "-";
    }

    return wrapped.units ? `${wrapped.value} ${wrapped.units}` : `${wrapped.value}`;
  }

  return formattedValue === undefined || formattedValue === null || formattedValue === ""
    ? "-"
    : `${formattedValue}`;
};

export const createFormattersObject = (fieldsFormat): FormatterMap => {
  const formatters: FormatterMap = {};
  fieldsFormat?.forEach((f) => {
    if (f.format !== undefined) {
      formatters[f.field] = readFormat(f.format);
    }
  });
  return formatters;
};

export const OPERATORCOLUMN: IColumnDefsConfig = {
  headerName: "Operator",
  key: "operator.value",
  minSize: 100,
  // customValueGetter: ({ row, key }) => getValueByPath(row, key)?.toUpperCase(),
  customValueGetter: ({ row, key }) => getValueByPath(row, key),
};

export const mapOptionToOperator: Record<string, string> = {
  Contains: "contains",
  "Does not contain": "notContains",
  Equals: "equals",
  "Does not equal": "notEqual",
  "Equals (number)": "equals",
  "Does not equal (number)": "notEqual",
  "Starts with": "startsWith",
  "Ends with": "endsWith",
  "Greater than": "greaterThan",
  "Greater than or equal to": "greaterThanOrEqual",
  "Less than": "lessThan",
  "Less than or equal to": "lessThanOrEqual",
  "Between (numbers)": "inRange",
  "Between (dates)": "inRange",
  Blank: "blank",
  "Not blank": "notBlank",
  After: "greaterThan",
  Before: "lessThan",
  True: "true",
  False: "false",
};

export const handleTableFilterChange = (
  updater: unknown,
  filters: ColumnFiltersState,
  filterFn: Record<string, string>,
) => {
  const next =
    typeof updater === "function"
      ? updater(filters.map((f) => ({ id: f.id, value: f.value })))
      : updater;
  const filtered = next.map((filter) => {
    const selectedOption = filterFn?.[filter.id];

    const operator = selectedOption
      ? mapOptionToOperator[selectedOption]
      : (filters.find((p) => p.id === filter.id)?.operator ?? "equals");

    return {
      id: filter.id,
      value: filter.value,
      operator,
    };
  });
  return filtered;
};

export const isDateLike = (value: string): boolean => {
  // Exclude numbers and numeric strings
  if (!value || (!isNaN(Number(value)) && value.trim() !== "")) {
    return false;
  }
  const date = Date.parse(value);
  return !isNaN(date);
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return null;
  }

  const pad = (num) => String(num).padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const getWrappedValue = (value: unknown): unknown => {
  if (
    value &&
    typeof value === "object" &&
    "value" in value &&
    Object.prototype.hasOwnProperty.call(value, "value")
  ) {
    return (value as { value?: unknown }).value;
  }

  return value;
};

export const normalizeDateTime = (value: unknown) => {
  const normalizedValue = getWrappedValue(value);

  return typeof normalizedValue === "string"
    ? normalizedValue.replace(
        /^(\d{4}\/\d{2}\/\d{2})-(\d{2}:\d{2}:\d{2})$/,
        "$1 $2",
      )
    : "";
};

export const formatUtcDate = (value?: string | null) => {
  if (!value) return "";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  const iso = parsed.toISOString();

  return iso.includes("T")
    ? `${iso.replace("T", " ").replace(".000Z", "").replace("Z", "")}`
    : `${iso}`;
};

/**
 * Opens a new window and renders a React component inside it, copying styles from the parent document.
 */
export const openComponentInNewWindow = (
  Component: React.ReactNode,
  title = "",
  width = 600,
  height = 800,
) => {
  const win = window.open(
    "",
    title,
    `width=${width},height=${height},left=200,top=200`,
  );
  if (win) {
    win.document.title = title;
    const mount = win.document.createElement("div");
    win.document.body.appendChild(mount);

    // Copy styles from parent
    Array.from(document.styleSheets).forEach((styleSheet) => {
      let css;
      try {
        css = styleSheet.cssRules;
      } catch (e) {
        return e;
      }
      if (css) {
        const style = win.document.createElement("style");
        for (let i = 0; i < css.length; i++) {
          style.appendChild(win.document.createTextNode(css[i].cssText));
        }
        win.document.head.appendChild(style);
      }
    });

    ReactDOM.createRoot(mount).render(Component);
  }
};
